<template>
  <div class="card report-options flex flex-column align-item-start">
    <p>
      Сравнивает показатели одной и той же группы между первичным и повторным проходами теста, показывая изменения после обучения или других вмешательств.
    </p>

    <div class="report-options__body">
      <div>
        <div class="label">
          Группа
        </div>

        <a-select
            v-model:value="selectedGroup"
            :placeholder="'Выберите группу'"
            style="width: 100%;"
            :not-found-content="'Нет данных'"
            show-arrow
        >
          <a-select-option
              v-for="item in groupOptions"
              :key="item.id"
              :value="item.id"
          >
            {{ item.text }}
          </a-select-option>
        </a-select>
      </div>

      <div class="date-block">
        <div>
          <div class="label">
            Дата первичной оценки
          </div>

          <a-date-picker
              v-model:value="date1"
              type="date"
              placeholder="Выберите дату"
              format="DD.MM.YYYY"
              value-format="YYYY-MM-DD"
              style="width: 100%"
          />
        </div>

        <div>
          <div class="label">
            Дата повторной оценки
          </div>

          <a-date-picker
              v-model:value="date2"
              type="date"
              placeholder="Выберите дату"
              format="DD.MM.YYYY"
              value-format="YYYY-MM-DD"
              style="width: 100%"
          />
        </div>
      </div>
    </div>

    <div class="report-options__btn flex justify-content-center">
      <a-button
          @click="requestForReport"
          class="primary-button"
          :disabled="!canGenerateReport"
      >
        Сформировать отчет
      </a-button>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeMount, onBeforeUnmount, ref } from "@vue/runtime-core";
import CustomSelect2 from "../../../components/Forms/Fields/CustomSelect2.vue";
import { useStore } from "vuex";
import { LeftOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { useRouter } from "vue-router";
import { getReportDetailsEmployeeCountText } from "./reportDetailsEmployeeCount";

export default {
  components: {
    LeftOutlined,
    CustomSelect2,
  },

  name: "general-dynamic-report-parametres",

  setup(props, context) {
    const store = useStore();
    const router = useRouter();

    const selectedGroup = ref(null);
    const groupOptions = ref([]);
    const date1 = ref(null);
    const date2 = ref(null);
    const lastGeneratedPayload = ref(null);

    const groups = computed(() => store.state.group.groups);
    const user = computed(() => store.state.profile.profile);

    const isReadyForReport = computed(() => {
      return selectedGroup.value !== null && date1.value && date2.value;
    });

    onBeforeMount(() => {
      store.dispatch("group/getGroups").then(() => {
        groupOptions.value = [
          {
            id: 0,
            text: "Все сотрудники",
            users_count: null,
          },
          ...groups.value.map((group) => {
            return {
              id: group.id,
              text: group.name,
              users_count: group.users_count,
            };
          }),
        ];
      });
    });

    const formatDateForDisplay = (value) => {
      if (!value) {
        return null;
      }

      const [year, month, day] = value.split("-");

      return `${day}.${month}.${year}`;
    };

    const parseBackendDate = (value) => {
      if (!value) {
        return null;
      }

      const [year, month, day] = value.split("-");

      return new Date(Number(year), Number(month) - 1, Number(day));
    };

    const getSelectedGroupObject = (groupId) => {
      if (groupId === 0) {
        return {
          id: 0,
          text: "Все сотрудники",
          users_count: null,
        };
      }

      const foundGroup = groups.value.find((item) => item.id === groupId);

      if (!foundGroup) {
        return {
          id: 0,
          text: "Все сотрудники",
          users_count: null,
        };
      }

      return {
        id: foundGroup.id,
        text: foundGroup.name,
        users_count: foundGroup.users_count,
      };
    };

    const getPayload = () => {
      const groupId = selectedGroup.value === 0 ? null : selectedGroup.value;

      const payload = {
        group_id: groupId,
        initial_assessment_date: date1.value,
        repeat_assessment_date: date2.value,
      };

      if (groupId === null) {
        payload.company_id = user.value?.company?.id ?? null;
      }

      return payload;
    };

    const getSelectedGroupCount = (group) => {
      return group.id === 0 ? "all" : group.users_count;
    };

    const serializePayload = (payload) => {
      return JSON.stringify(payload);
    };

    const isReportAlreadyGenerated = computed(() => {
      if (!lastGeneratedPayload.value) {
        return false;
      }

      return lastGeneratedPayload.value === serializePayload(getPayload());
    });

    const canGenerateReport = computed(() => {
      return isReadyForReport.value && !isReportAlreadyGenerated.value;
    });

    const requestForReport = () => {
      if (!isReadyForReport.value) {
        return;
      }

      const firstDate = parseBackendDate(date1.value);
      const secondDate = parseBackendDate(date2.value);

      if (!firstDate || !secondDate) {
        message.error("Выберите даты");
        return;
      }

      const payload = getPayload();

      store.dispatch("group/clearAnalyticsGroupData");

      const selectedGroupObject = getSelectedGroupObject(selectedGroup.value);

      const firstDateString = formatDateForDisplay(date1.value);
      const secondDateString = formatDateForDisplay(date2.value);
      const dateInterval = `${firstDateString} / ${secondDateString}`;

      store.dispatch("group/setAnalyticsGroupData", {
        group_1: {
          name: selectedGroupObject.text,
          count: selectedGroupObject.users_count ?? null,
          dateInterval,
        },
        group_2: null,
      });

      const reportDetails = {
        groupName: selectedGroupObject.text,
        filterName: "",
        employeeCount: getReportDetailsEmployeeCountText(getSelectedGroupCount(selectedGroupObject)),
        dateString: dateInterval,
        dateInterval,
        type: "dynamic",
      };

      store.commit("analytics/setCurrentReportDetails", reportDetails);

      lastGeneratedPayload.value = serializePayload(payload);

      context.emit("reportRequest", payload);
    };

    onBeforeUnmount(() => {
      store.commit("analytics/clearCurrentReportDetails");
    });

    return {
      selectedGroup,
      groupOptions,
      groups,
      date1,
      date2,
      isReadyForReport,
      canGenerateReport,
      requestForReport,
      router,
    };
  },
};
</script>

<style scoped lang="scss">
.header {
  position: fixed;
  top: 0;
  padding: 15px 10px 0 10px;
  display: flex;
  justify-content: center;
  row-gap: 12px;
  column-gap: 25px;
  font-size: 20px;
  font-weight: 600;
  z-index: 25;
}

.icon-back {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 24px;
  width: 24px;
  height: 24px;
  background-color: #FFFFFF;
  border-radius: 8px;
  cursor: pointer;

  .icon-arrow {
    width: 14px;
    height: 14px;
  }

  &:hover {
    .icon-arrow {
      color: var(--main-green);
    }
  }
}

.card {
  p {
    padding-left: 0;
    font-size: 14px;
  }
}

.label {
  font-size: 14px;
}

.report-options__body {
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  max-width: 590px;
  width: 100%;

  @media(max-width: $tablet-breakpoint) {
    max-width: 100%;
  }
}

.date-block {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 10px;
}

@media (max-width: $mobile-breakpoint) {
  .report-options__btn,
  .primary-button {
    width: 100%;
  }

  .date-block {
    grid-template-columns: 1fr;
    row-gap: 12px;
  }
}
</style>
